import Aes from 'react-native-aes-crypto';

/**
 * Step 10.2.1 — client-side hashing of invitation validation answers.
 *
 * Before: inviter + invitee both POSTed the plaintext answer; the
 * backend argon2-hashed on receipt (SECURITY_AUDIT.md §10.2.1 HIGH).
 *
 * After: inviter's client generates a random salt and computes a PBKDF2
 * verifier over the normalized answer; the pair is stored on the
 * invitation row. The invitee's client fetches the salt via
 * /api/invitations/verify-token, types their answer, recomputes the
 * verifier locally, and POSTs only the verifier to /accept. Plaintext
 * never leaves the device.
 *
 * Primitives are the same ones DocumentEncryptionV2 already uses
 * (react-native-aes-crypto native PBKDF2-SHA256 + randomKey). No new
 * native dependency.
 */

const SALT_BYTES = 16;
const SALT_HEX_LEN = SALT_BYTES * 2;

const VERIFIER_BYTES = 32;
const VERIFIER_HEX_LEN = VERIFIER_BYTES * 2;
// react-native-aes-crypto's native pbkdf2 takes the output length in BITS,
// not bytes (see node_modules/react-native-aes-crypto/ios/Aes/lib/AesCrypt.m
// — `dataWithLength:length/8`). randomKey() in the same module uses bytes.
// Don't confuse the two — pass _BITS to pbkdf2, _BYTES to randomKey.
const VERIFIER_BITS = VERIFIER_BYTES * 8;

// Lower than the master-key-derivation iteration count because the
// verifier is used in a lower-value context (invitation acceptance) and
// the answer space is narrower (short strings with guessable entropy)
// so the marginal cost per guess matters less than UX responsiveness.
// Still comfortably above OWASP 2013 minimum (10k) — just below the
// OWASP-2024-for-passwords 600k value.
export const VALIDATION_VERIFIER_ITERATIONS = 100000;

// Minimum acceptable validation-answer length, applied AFTER normalization
// (trim+lowercase). Enforced on the inviter side so an invitee never has
// to type a single-character or whitespace-only answer to match. Exported
// so both inviter screens and any future caller can share the rule.
export const MIN_VALIDATION_ANSWER_LENGTH = 3;

/**
 * Normalize an answer before hashing so a user who answered "Buddy "
 * on their phone and "buddy" on their desktop lands on the same
 * verifier. Matches the old backend normalization.
 */
export function normalizeValidationAnswer(raw: string): string {
  return raw.trim().toLowerCase();
}

/**
 * Fresh per-invitation salt. 16 bytes = 128 bits of salt, enough to
 * defeat rainbow-table attacks against any realistic answer space.
 */
export async function generateValidationSalt(): Promise<string> {
  return Aes.randomKey(SALT_BYTES);
}

/**
 * Compute the verifier the backend will store (and later compare
 * against). Must be deterministic for the same inputs so the invitee
 * produces the same value as the inviter.
 */
export async function hashValidationAnswer(
  answer: string,
  saltHex: string,
): Promise<string> {
  if (!answer || typeof answer !== 'string') {
    throw new Error('validation answer is required');
  }
  if (
    typeof saltHex !== 'string' ||
    saltHex.length !== SALT_HEX_LEN ||
    !/^[0-9a-f]+$/.test(saltHex.toLowerCase())
  ) {
    throw new Error(
      `validation salt must be ${SALT_HEX_LEN} hex chars (got ${
        typeof saltHex === 'string' ? saltHex.length : 'non-string'
      })`,
    );
  }
  const normalized = normalizeValidationAnswer(answer);
  const derived = await Aes.pbkdf2(
    normalized,
    saltHex.toLowerCase(),
    VALIDATION_VERIFIER_ITERATIONS,
    VERIFIER_BITS,
    'sha256',
  );
  if (
    typeof derived !== 'string' ||
    derived.length !== VERIFIER_HEX_LEN
  ) {
    // User-readable wording. The screens that call this surface the
    // thrown message directly via Alert (see AddSuccessorScreen et al.),
    // so the message must read sensibly to end users. The underlying
    // native error is captured by the screen's logger.error before
    // showing the alert, so debugging info is not lost.
    throw new Error("We couldn't secure your validation answer. Please try a different answer.");
  }
  return derived.toLowerCase();
}
